import { useEffect, useRef, useState } from 'react'
import type { ComponentProps } from 'react'
import copy from 'copy-to-clipboard'
import { Check, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

export interface CopyTextProps
  extends Omit<ComponentProps<typeof Button>, 'onClick'> {
  /** String written to the clipboard when the control is activated. */
  text: string
  /** Optional success toast; set to `null` to disable. */
  copiedToast?: string | null
}

/**
 * Button that copies `text` to the clipboard using copy-to-clipboard.
 */
const CopyText = ({
  text,
  children,
  className,
  variant = 'ghost',
  size = 'icon',
  'aria-label': ariaLabelProp,
  ...buttonProps
}: CopyTextProps) => {
  const [copied, setCopied] = useState(false)
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    }
  }, [])

  const handleClick = () => {
    const ok = copy(text)
    if (ok) {
      setCopied(true)
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
      resetTimerRef.current = setTimeout(() => {
        setCopied(false)
        resetTimerRef.current = null
      }, 2000)
    } else {
      toast.error('Could not copy')
    }
  }

  return (
    <Button
      type='button'
      variant={variant}
      size={size}
      className={cn(className)}
      onClick={handleClick}
      aria-label={copied ? 'Copied' : (ariaLabelProp ?? 'Copy to clipboard')}
      {...buttonProps}
    >
      <span className='inline-grid place-items-center'>
        <span
          className={cn(
            'col-start-1 row-start-1 transition-[transform,opacity] duration-200 ease-out',
            copied
              ? 'pointer-events-none scale-75 opacity-0'
              : 'scale-100 opacity-100'
          )}
          aria-hidden={copied}
        >
          {children ?? <Copy />}
        </span>
        <span
          className={cn(
            'col-start-1 row-start-1 transition-[transform,opacity] duration-200 ease-out',
            copied
              ? 'scale-100 opacity-100'
              : 'pointer-events-none scale-75 opacity-0'
          )}
          aria-hidden={!copied}
        >
          <Check className='text-emerald-600' aria-hidden />
        </span>
      </span>
    </Button>
  )
}

export default CopyText
